/**
 * WF-Options - Option group component for wizard forms
 *
 * Manages a group of wf-option elements with single/multi-select support.
 * Auto-assigns keyboard shortcuts to child options.
 *
 * @example
 * <!-- Single select (default) -->
 * <wf-options name="role" required>
 *   <wf-option value="developer">Developer</wf-option>
 *   <wf-option value="designer">Designer</wf-option>
 * </wf-options>
 *
 * <!-- Multi-select with constraints -->
 * <wf-options name="interests" multi required min="1" max="3">
 *   <wf-option value="ai">AI</wf-option>
 *   <wf-option value="web">Web</wf-option>
 *   <wf-option value="mobile">Mobile</wf-option>
 * </wf-options>
 */

import { html, type CSSResultGroup } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';

import type { WfOption, OptionSelectDetail } from './wf-option.js';
import type { WfOther, OtherChangeDetail } from './wf-other.js';
import { WfLayout, LayoutMode } from './wf-layout.js';
import { wfOptionsStyles } from './wf-options.styles.js';
import './wf-badge.js';
import './wf-other.js';

// Event detail types
export interface OptionsChangeDetail {
  name: string;
  value: string | string[];
  selected: string[];
  /** Extra fields for dual-field submission (e.g., "Other" with custom text) */
  extraFields?: Record<string, unknown>;
}

@customElement('wf-options')
export class WfOptions extends WfLayout {
  // Override styles to add wf-options specific styles
  static override styles: CSSResultGroup = [WfLayout.styles, wfOptionsStyles];

  /**
   * Slot reference for discovering options
   */
  @query('slot:not([name])')
  private _defaultSlot!: HTMLSlotElement;

  /**
   * Override layout mode to grid by default
   */
  @property({ type: String })
  override mode: LayoutMode = 'grid';

  /**
   * Override gap to 16px (md) by default for options grid
   */
  @property({ type: String })
  override gap = 'md';

  /**
   * Field name for form data
   */
  @property({ type: String })
  name = '';

  /**
   * Enable multi-select mode
   */
  @property({ type: Boolean })
  multi = false;

  /**
   * Whether selection is required
   */
  @property({ type: Boolean })
  required = false;

  /**
   * Minimum selections required (multi-select only)
   */
  @property({ type: Number })
  min = 0;

  /**
   * Maximum selections allowed (multi-select only, 0 = unlimited)
   */
  @property({ type: Number })
  max = 0;

  /**
   * Number of grid columns (2 by default)
   * Override type to match WfLayout's string type
   */
  @property({ type: String, reflect: true })
  override columns = '2';

  /**
   * Currently selected values
   */
  @state()
  private _selected: Set<string> = new Set();

  /**
   * Value of the "Other" text input
   */
  @state()
  private _otherValue = '';

  /**
   * Validation error message
   */
  @state()
  private _errorMessage = '';

  /**
   * Whether validation has been activated (after first Next press)
   */
  private _validationActivated = false;

  /**
   * Child option elements
   */
  private _options: WfOption[] = [];

  /**
   * Keyboard shortcut map (key -> option)
   */
  private _shortcutMap: Map<string, WfOption> = new Map();

  /**
   * Reference to composable wf-other child (if present)
   */
  private _composableOther: WfOther | null = null;

  connectedCallback(): void {
    super.connectedCallback();
    // Ensure clean state on mount
    this._errorMessage = '';
    this._validationActivated = false;
    // Listen for option selection events
    this.addEventListener('wf-option-select', this._handleOptionSelect as EventListener);
    // Listen for composable wf-other-change events
    this.addEventListener('wf-other-change', this._handleComposableOtherChange as EventListener);
    // Listen for keyboard events at document level so shortcuts work globally
    document.addEventListener('keydown', this._handleKeydown);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.removeEventListener('wf-option-select', this._handleOptionSelect as EventListener);
    this.removeEventListener('wf-other-change', this._handleComposableOtherChange as EventListener);
    document.removeEventListener('keydown', this._handleKeydown);
  }

  firstUpdated(changedProperties: Map<string, unknown>): void {
    // Call parent to ensure layout styles are applied
    super.firstUpdated(changedProperties);

    // Detect composable wf-other child from slotted elements
    const slottedElements = this._defaultSlot?.assignedElements({ flatten: true }) || [];
    const foundOther = slottedElements.find(
      (el) => el.tagName.toLowerCase() === 'wf-other'
    );
    this._composableOther = foundOther ? (foundOther as WfOther) : null;

    this._discoverOptions();

    // Force re-render to ensure _otherShortcut badge appears
    this.requestUpdate();
  }

  /**
   * Check if this component has a composable wf-other child
   */
  private _hasComposableOther(): boolean {
    return this._composableOther !== null;
  }

  updated(changedProperties: Map<string, unknown>): void {
    // Call parent to apply layout styles
    super.updated(changedProperties);

    if (changedProperties.has('multi') || changedProperties.has('max')) {
      // Just update option states, don't validate (lazy validation)
      this._updateOptionStates();
    }
  }

  render() {
    // Apply layout styles to host element (inherited from WfLayout)
    super.updated(new Map());

    // Set ARIA attributes on host
    this.setAttribute('role', 'listbox');
    this.setAttribute('aria-multiselectable', String(this.multi));
    this.setAttribute('aria-required', String(this.required));

    // Shadow DOM: slot for wf-option children
    return html`
      <slot @slotchange="${this._handleSlotChange}"></slot>
      <div class="wf-error-wrapper ${this._errorMessage ? 'wf-has-error' : ''}">
        <span class="wf-error-message" role="alert" aria-live="polite" data-testid="wf-error">
          ${this._errorMessage || ''}
        </span>
      </div>
    `;
  }

  /**
   * Handle slot content changes to rediscover options
   */
  private _handleSlotChange(): void {
    // Update composable wf-other reference
    const slottedElements = this._defaultSlot?.assignedElements({ flatten: true }) || [];
    const foundOther = slottedElements.find(
      (el) => el.tagName.toLowerCase() === 'wf-other'
    );
    this._composableOther = foundOther ? (foundOther as WfOther) : null;

    // Rediscover options
    this._discoverOptions();
  }

  /**
   * Handle change events from composable wf-other child
   */
  private _handleComposableOtherChange = (e: CustomEvent<OtherChangeDetail>): void => {
    e.stopPropagation();
    this._otherValue = e.detail.value;

    // When typing in "Others", deselect any selected options (mutually exclusive)
    if (this._otherValue.trim() && this._selected.size > 0) {
      this._selected.clear();
      this._updateOptionStates();
    }

    this._dispatchChange();
  };

  private _discoverOptions(): void {
    // Shadow DOM: Get wf-option children from slot's assigned elements
    const slottedElements = this._defaultSlot?.assignedElements({ flatten: true }) || [];
    this._options = slottedElements.filter(
      (el) => el.tagName.toLowerCase() === 'wf-option'
    ) as WfOption[];

    // Assign keyboard shortcuts (A, B, C, etc.)
    this._shortcutMap.clear();
    this._options.forEach((option, index) => {
      if (index < 26) {
        const shortcut = String.fromCharCode(65 + index); // A, B, C...
        option.shortcut = shortcut;
        this._shortcutMap.set(shortcut, option);
      }

      // Restore selected state if value is in _selected
      option.setSelected(this._selected.has(option.value));
    });

    // Assign shortcut to composable wf-other child
    if (this._composableOther && this._options.length < 26) {
      const otherShortcut = String.fromCharCode(65 + this._options.length);
      this._composableOther.shortcut = otherShortcut;
    }
  }

  private _handleOptionSelect = (e: CustomEvent<OptionSelectDetail>): void => {
    // Skip if this is a forwarded event (already processed, just bubbling to wizard-form)
    if ((e.detail as OptionSelectDetail & { forwarded?: boolean }).forwarded) {
      return;
    }

    e.stopPropagation();
    const { value } = e.detail;
    this._selectValue(value);

    // Re-dispatch for wizard-form auto-advance (single-select only)
    if (!this.multi) {
      this.dispatchEvent(
        new CustomEvent('wf-option-select', {
          detail: { ...e.detail, forwarded: true },
          bubbles: true,
          composed: true,
        })
      );
    }
  };

  private _selectValue(value: string): void {
    if (this.multi) {
      this._handleMultiSelect(value);
    } else {
      this._handleSingleSelect(value);
    }

    // When selecting an option, clear the "Others" text (mutually exclusive)
    if (this._otherValue) {
      this._otherValue = '';
      // Also clear composable wf-other if present
      if (this._composableOther) {
        this._composableOther.clear();
      }
    }

    this._updateOptionStates();
    this._validateSelection();
    this._dispatchChange();
  }

  private _handleSingleSelect(value: string): void {
    // Clear previous selection and select new
    this._selected.clear();
    this._selected.add(value);
  }

  private _handleMultiSelect(value: string): void {
    if (this._selected.has(value)) {
      // Deselect
      this._selected.delete(value);
    } else {
      // Check max constraint
      if (this.max > 0 && this._selected.size >= this.max) {
        // At max capacity, don't add more
        return;
      }
      this._selected.add(value);
    }
  }

  private _updateOptionStates(): void {
    this._options.forEach((option) => {
      option.setSelected(this._selected.has(option.value));
    });
  }

  /**
   * Silent validation check (no side effects)
   */
  private _checkValidation(): boolean {
    // Check for "Others" text from composable wf-other
    const hasOtherValue = this._hasComposableOther() && this._otherValue.trim();
    const hasSelection = this._selected.size > 0 || hasOtherValue;

    if (this.required && !hasSelection) {
      return false;
    }

    if (this.multi) {
      if (this.min > 0 && this._selected.size < this.min && !hasOtherValue) {
        return false;
      }

      if (this.max > 0 && this._selected.size > this.max) {
        return false;
      }
    }

    return true;
  }

  /**
   * Validate and show error messages (only when activated)
   */
  private _validateSelection(): boolean {
    // Only show error messages if validation has been activated
    if (!this._validationActivated) {
      // Clear any stale error message
      this._errorMessage = '';
      return this._checkValidation();
    }

    this._errorMessage = '';

    // Check for "Others" text from composable wf-other
    const hasOtherValue = this._hasComposableOther() && this._otherValue.trim();
    const hasSelection = this._selected.size > 0 || hasOtherValue;

    if (this.required && !hasSelection) {
      this._errorMessage = this._hasComposableOther()
        ? 'Please select an option or specify in the text field'
        : 'Please select an option';
      return false;
    }

    if (this.multi) {
      if (this.min > 0 && this._selected.size < this.min && !hasOtherValue) {
        this._errorMessage = `Please select at least ${this.min} option${this.min > 1 ? 's' : ''}`;
        return false;
      }

      if (this.max > 0 && this._selected.size > this.max) {
        this._errorMessage = `Please select at most ${this.max} option${this.max > 1 ? 's' : ''}`;
        return false;
      }
    }

    return true;
  }

  private _dispatchChange(): void {
    const selectedArray = Array.from(this._selected);

    const detail: OptionsChangeDetail = {
      name: this.name,
      value: this.value,
      selected: selectedArray,
    };

    // Add extraFields for composable wf-other
    if (this._hasComposableOther() && this._otherValue.trim() && this._selected.size === 0) {
      const otherName = this._composableOther?.name || `${this.name}_other`;
      detail.extraFields = {
        [otherName]: this._otherValue,
      };
      // Use wf-other's parentValue attribute for parent field (defaults to "Others")
      detail.value = this._composableOther?.parentValue || 'Others';
    }

    this.dispatchEvent(
      new CustomEvent('wf-change', {
        detail,
        bubbles: true,
        composed: true,
      })
    );
  }

  private _handleKeydown = (e: KeyboardEvent): void => {
    // Only handle shortcuts if this component is in the active step
    const parentStep = this.closest('wf-step');
    if (!parentStep || !parentStep.hasAttribute('active')) {
      return;
    }

    // Don't handle A-Z shortcuts if user is typing in an input field
    // Use composedPath to get actual target (works with Shadow DOM)
    const path = e.composedPath();
    const actualTarget = path[0] as HTMLElement;
    if (actualTarget?.tagName === 'INPUT' || actualTarget?.tagName === 'TEXTAREA') {
      return;
    }

    // Handle A-Z keys for shortcuts
    const key = e.key.toUpperCase();
    if (key.length === 1 && key >= 'A' && key <= 'Z') {
      // Check if this is the composable wf-other shortcut
      if (this._composableOther && key === this._composableOther.shortcut) {
        e.preventDefault();
        this._composableOther.focusInput();
        return;
      }

      const option = this._shortcutMap.get(key);
      if (option && !option.disabled) {
        e.preventDefault();
        option.triggerSelect();
      }
    }
  };

  // ============================================
  // Public API
  // ============================================

  /**
   * Get current value (string for single, array for multi)
   * If wf-other child is present and has text, returns the custom text
   */
  get value(): string | string[] {
    // If "Others" text has value and no options selected, return the custom text
    if (this._hasComposableOther() && this._otherValue.trim() && this._selected.size === 0) {
      return this.multi ? [this._otherValue] : this._otherValue;
    }

    const selectedArray = Array.from(this._selected);

    if (this.multi) {
      return selectedArray;
    }

    // For single-select
    return selectedArray[0] ?? '';
  }

  /**
   * Set value programmatically
   */
  set value(val: string | string[]) {
    this._selected.clear();

    if (Array.isArray(val)) {
      val.forEach((v) => this._selected.add(v));
    } else if (val) {
      this._selected.add(val);
    }

    this._updateOptionStates();
    this._validateSelection();
  }

  /**
   * Get selected values as array
   */
  getSelected(): string[] {
    return Array.from(this._selected);
  }

  /**
   * Check if option is selected
   */
  isSelected(value: string): boolean {
    return this._selected.has(value);
  }

  /**
   * Select an option by value
   */
  select(value: string): void {
    this._selectValue(value);
  }

  /**
   * Deselect an option by value
   */
  deselect(value: string): void {
    if (this._selected.has(value)) {
      this._selected.delete(value);
      this._updateOptionStates();
      this._validateSelection();
      this._dispatchChange();
    }
  }

  /**
   * Clear all selections
   */
  clear(): void {
    this._selected.clear();
    this._updateOptionStates();
    this._validateSelection();
    this._dispatchChange();
  }

  /**
   * Validate current selection
   */
  validate(): boolean {
    // Activate validation mode (enables showing error messages)
    this._validationActivated = true;
    return this._validateSelection();
  }

  /**
   * Check if selection is valid (no side effects)
   */
  get isValid(): boolean {
    return this._checkValidation();
  }

  /**
   * Show error message
   */
  showError(message: string): void {
    this._errorMessage = message;
  }

  /**
   * Clear error message
   */
  clearError(): void {
    this._errorMessage = '';
  }

  /**
   * Focus first option
   */
  focus(): void {
    this._options[0]?.focus();
  }

  /**
   * Reset to initial state
   */
  reset(): void {
    this._selected.clear();
    this._otherValue = '';
    this._errorMessage = '';
    this._validationActivated = false;

    // Clear composable wf-other if present
    if (this._composableOther) {
      this._composableOther.clear();
    }

    // Restore any pre-selected options from attributes
    this._options.forEach((option) => {
      if (option.hasAttribute('selected')) {
        this._selected.add(option.value);
      }
      option.setSelected(this._selected.has(option.value));
    });

    this._dispatchChange();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'wf-options': WfOptions;
  }
}
